// Central content module — sourced from JJ Argent's CV and problem statement.

export const BRAND = {
  name: "JJ Argent",
  fullName: "JJ Argent",
  tagline: "Built to run without me.",
  support:
    "I turn complex, people-dependent work into clear, scalable systems — bringing together people leadership, operational improvement, digital transformation and practical automation to make work simpler and experiences better.",
  location: "Melbourne, Australia · Internationally mobile",
  email: "contact@jjargent.com",
  phone: "0447 563 578",
  phoneHref: "tel:+61447563578",
  linkedin: "https://www.linkedin.com/in/jesse-argent-771472171",
  portrait: "/brand-kit/jj-portrait.jpg",
  portraitSrcSet:
    "/brand-kit/jj-portrait-640.webp 640w, /brand-kit/jj-portrait-960.webp 960w, /brand-kit/jj-portrait-1280.webp 1280w, /brand-kit/jj-portrait-1600.webp 1600w",
  portraitSrcSetJpg:
    "/brand-kit/jj-portrait-640.jpg 640w, /brand-kit/jj-portrait-960.jpg 960w, /brand-kit/jj-portrait-1280.jpg 1280w, /brand-kit/jj-portrait-1600.jpg 1600w",
  capabilityLine:
    "Program Leadership · Major Events · Operational Systems · Accessibility · Program Design · AI-Enabled Workflows",
};

export const IMPACT_METRICS = [
  {
    id: "annual-events",
    value: "525+",
    label: "Events delivered annually",
    context: "University of Melbourne Union — Events, Sponsorship & Advertising",
    detail:
      "Directed a 525+ event annual portfolio across a 5-day Orientation Week, campus activations, cultural programs and student-experience initiatives — reaching 145,000+ attendees.",
  },
  {
    id: "food-relief",
    value: "8,000+",
    label: "Weekly stakeholder reach",
    context: "University of Melbourne — Food Relief Program",
    detail:
      "Grew a university food-relief program from 400 to 8,000+ weekly active registrations. Negotiated a dedicated commercial building footprint and led end-to-end logistics as sole coordinator for 12 months.",
  },
  {
    id: "sponsorship-growth",
    value: "200%",
    label: "Sponsorship growth · $1.2M secured",
    context: "University of Melbourne Union",
    detail:
      "Spearheaded commercial strategy that lifted sponsorship revenue 200% — from a $400K baseline to $1.2M in 1.5 years. Revenue directly funded program expansion and long-term financial sustainability across 20+ academic, not-for-profit and corporate partnerships.",
  },
  {
    id: "calgary-budget",
    value: "$1.75M",
    label: "Festival budget stewarded",
    context: "Calgary Pride — Production & Programming",
    detail:
      "Directed end-to-end production for a 24-event festival attracting 145,000+ attendees. Full stewardship of a $1.75M operational budget with 100% permit compliance. Led 9 staff, 40–45 contractors and 230+ volunteers.",
  },
  {
    id: "toronto-portfolio",
    value: "94 FTE",
    label: "17 buildings · $2.8M budget",
    context: "University of Toronto — Ancillary Services Operations",
    detail:
      "Strategic leadership across a 17-building precinct with a 94 FTE unionised team and a $2.8M operational budget. Championed end-to-end EventPro implementation, standardised procedures and 100% compliance with health, safety and customer-service mandates.",
  },
  {
    id: "attendee-satisfaction",
    value: "+35%",
    label: "Attendee satisfaction lift",
    context: "University of Melbourne Union",
    detail:
      "Implemented robust participant feedback systems and continuous-improvement loops that lifted attendee satisfaction by 35% across a 525+ event annual portfolio — evidencing a data-driven audience development practice.",
  },
];

export const AUDIENCE_ROUTES = [
  {
    id: "recruit",
    label: "I'm hiring for a senior role",
    heading: "You need a senior operator.",
    body:
      "13+ years leading complex programs, major events, operational transformation and cross-functional teams across universities, festivals and community organisations in Australia and Canada. Australian citizen with unrestricted work rights and internationally mobile.",
    proof: ["Monash · Melbourne · Toronto", "$1.2M+ sponsorship generated", "300+ volunteers per semester"],
    ctaLabel: "See experience",
    ctaHref: "/for-recruiters",
  },
  {
    id: "hire",
    label: "I need a program or operations partner",
    heading: "You need a program that lasts.",
    body:
      "Program Resilience Reviews, Event Operating System builds, and interim leadership. Turning scattered spreadsheets and personal knowledge into governed, repeatable workflows.",
    proof: ["Documented SOPs", "Salesforce · Eventpro · Qualtrics", "Governance & RACI models"],
    ctaLabel: "Explore services",
    ctaHref: "/services",
  },
  {
    id: "ai",
    label: "I'm exploring practical AI",
    heading: "You want AI that actually works in a university.",
    body:
      "Practical, responsibly-scoped AI implementation for higher education. Workflow analysis, accessibility, governance and managed rollout — under Services.",
    proof: ["Higher-ed first", "Google AI Fundamentals", "Accessibility-first"],
    ctaLabel: "Explore JJ AI Solutions",
    ctaHref: "/ai-solutions",
  },
  {
    id: "speak",
    label: "I'm planning a talk or workshop",
    heading: "You need a speaker with real operational stories.",
    body:
      "Keynotes and workshops on resilient programs, scaling events and practical AI for university operations.",
    proof: ["Built to Run Without Me", "From a Carpark to 8,000+", "Practical AI for University Operations"],
    ctaLabel: "Enquire about a talk",
    ctaHref: "/contact?type=speaking",
  },
];

export const CAPABILITIES = [
  {
    n: "01",
    title: "Program & Project Leadership",
    body:
      "Designing, coordinating and delivering complex programs involving multiple teams, competing priorities and high stakeholder expectations.",
  },
  {
    n: "02",
    title: "Major Events & Activations",
    body:
      "Leading high-volume orientation programs, festivals, activations, community events and participant experiences at scale.",
  },
  {
    n: "03",
    title: "Operational Systems",
    body:
      "Turning scattered spreadsheets, personal knowledge and manual processes into documented, governed and repeatable workflows.",
  },
  {
    n: "04",
    title: "Student Experience & Inclusion",
    body:
      "Developing services and programs for students, underrepresented communities, people with disability, refugees and humanitarian entrants.",
  },
  {
    n: "05",
    title: "Workforce & Volunteer Leadership",
    body:
      "Leading distributed teams, casual workforces, volunteers and multidisciplinary operational groups across time zones.",
  },
  {
    n: "06",
    title: "Practical AI & Workflow Design",
    body:
      "Identifying safe, useful and realistic opportunities to improve administrative work through AI, automation and better process design.",
  },
];

export const CARPARK_STAGES = [
  {
    n: "01",
    title: "The starting point",
    body:
      "A small food-relief initiative operating from a carpark, using a folding table and a spreadsheet. 400 weekly registrations.",
  },
  {
    n: "02",
    title: "The scale challenge",
    body:
      "Rapidly growing registrations, increasing student need, volunteers, suppliers, logistics and service complexity — all landing on one coordinator.",
  },
  {
    n: "03",
    title: "The system redesign",
    body:
      "Student intake, triage, volunteer management, supplier relationships, CRM and data management, referrals, grants, documentation, SOPs and continuity planning — all connected.",
  },
  {
    n: "04",
    title: "The real test",
    body:
      "The system continued functioning even when JJ could not physically operate it. 8,000+ students supported weekly.",
  },
  {
    n: "05",
    title: "The lesson",
    body:
      "A successful program is not one that survives because of a heroic individual. It succeeds because the system has been designed to carry the work.",
  },
];

export const EXPERIENCE = [
  {
    org: "Australian Services Union",
    role: "Member Engagement & Digital Transformation Manager",
    period: "Sep 2026 – Present",
    location: "Melbourne, Australia",
    tags: ["Leadership", "Digital Transformation", "Operations", "Programs & Projects"],
    overview:
      "Victorian & Tasmanian Authorities & Services Branch. Leading member engagement operations, frontline service delivery and practical digital transformation across the branch.",
    bullets: [
      "Lead the Member Engagement Team across frontline enquiries, membership administration and service delivery — introducing structured team huddles, workload visibility and staff development conversations.",
      "Review and improve how member enquiries, membership administration and Industrial Support referrals move through the organisation — clearer triage, allocation and escalation.",
      "Oversee the use of iMIS and connected workplace technologies (Microsoft 365, Teams, SharePoint), investigating data quality and developing more consistent CRM documentation.",
      "Design and test Power Automate workflows for member referrals, follow-up and overdue monitoring — validating with fictional records before any live operational use.",
      "Develop practical staff guides, templates and operational instructions to support consistent service delivery and member experience.",
      "Introduce structured booking capacity, manager-approved escalations and continuity rules so open and closed cases don't fall through the cracks.",
    ],
  },
  {
    org: "Monash University",
    role: "Senior Coordinator — Projects & Development",
    period: "Mar 2026 – Aug 2026",
    location: "Melbourne, Australia",
    tags: ["Leadership", "Programs & Projects", "Community Engagement"],
    overview:
      "Access, Inclusion & Success — fixed-term appointment leading project and development work across five student portfolios.",
    bullets: [
      "Directed end-to-end planning for Monash Open Day across five student portfolios, aligning access, peer learning, disability support and development teams within a single operating plan.",
      "Led recruitment, onboarding and coordination of 300+ student staff and volunteers per semester across university-wide activations and services.",
      "Managed annual multi-channel communications planning across five departments in partnership with central marketing, translating complex service priorities into clear audience journeys.",
      "Designed registration, feedback and focus-group infrastructure using Qualtrics and related workflows to improve insight, accessibility and support for priority student cohorts.",
      "Convened senior internal stakeholders and external partners to resolve dependencies, clarify ownership and deliver compliant, accessible outcomes across multiple workstreams.",
    ],
  },
  {
    org: "Argent Insured Industries",
    role: "Business Manager",
    period: "Oct 2025 – Current",
    location: "Remote – Cairns",
    tags: ["Leadership", "Operations"],
    bullets: [
      "Direct strategic planning and daily operations for a growing security business, market development, team leadership and compliance management.",
      "Develop and execute strategic business plans that identify and secure new opportunities across the local security market, driving revenue growth and regional expansion.",
      "Serve as primary advisor to key clients and partners; lead sales presentations and contract negotiations.",
      "Manage budgets, forecast revenue and prepare financial reports to ensure profitability and fiscal sustainability.",
    ],
  },
  {
    org: "University of Toronto Portfolio",
    role: "Business Manager, Ancillary Services Operations",
    period: "Nov 2024 – Sep 2025",
    location: "Toronto, Canada",
    tags: ["Leadership", "Operations", "Digital Transformation", "Programs & Projects"],
    bullets: [
      "Led operational delivery across a 17-building portfolio supporting a 94 FTE unionised workforce and $2.8M operating environment, coordinating services across facilities, security, IT, catering and student-facing teams.",
      "Partnered with software developers to configure EventPro as a unified bookings and operational platform, connecting billing, dietary requirements, client follow-up and reporting workflows.",
      "Authored SOPs, technical manuals and video training resources that converted undocumented knowledge into repeatable processes for staff and student associations.",
      "Chaired cross-campus operational meetings, resolved allocation conflicts and directed logistics for major programs including the Toronto International Festival of Authors, graduation ceremonies, guest lectures and open days.",
    ],
  },
  {
    org: "Calgary Pride",
    role: "Manager, Production & Programming",
    period: "Jul 2024 – Jan 2025",
    location: "Calgary, Canada",
    tags: ["Major Events", "Operations", "Community Engagement", "Leadership"],
    bullets: [
      "Directed production and programming across a 24-event festival portfolio reaching 145,000+ attendees, including the city-centre parade and two-day flagship festival.",
      "Coordinated multi-stage infrastructure, licensed areas, sponsor activations, community markets, performers and city-wide operational dependencies within a $1.75M festival environment at Prince's Island Park.",
      "Built trusted relationships across government, emergency services, advocacy groups, sponsors, artists and contractors — using early engagement to resolve politically sensitive issues while protecting safety, inclusion and uninterrupted delivery.",
      "Led core staff, contractors and 230+ volunteers through peak operations, maintaining clear escalation pathways and calm decision-making under time-critical conditions.",
    ],
  },
  {
    org: "University of Melbourne (UMSU)",
    role: "Division Manager, Events, Advertising & Sponsorship",
    period: "Aug 2022 – Jun 2024",
    location: "Melbourne, Australia",
    tags: ["Major Events", "Leadership", "Programs & Projects"],
    bullets: [
      "Held division-wide accountability for 525+ annual events and a workforce of 4 FTE, 3 part-time employees, 25 casual staff and 160+ volunteers.",
      "Directed the five-day Orientation festival across nine precinct buildings, two theatres, an outdoor amphitheatre and 200+ club and commercial activations, plus satellite campus programs.",
      "Built and managed a sponsorship portfolio exceeding $1.2M in cash and in-kind value through naming rights, activation packages and multi-year partnerships.",
      "Established operating frameworks for approvals, vendors, staffing, risk, accessibility and cross-campus delivery while aligning faculties, central marketing, accommodation providers, clubs and external partners.",
    ],
  },
  {
    org: "University of Melbourne (SASS)",
    role: "Project Manager & Food Relief Coordinator",
    period: "Aug 2021 – Nov 2022",
    location: "Melbourne, Australia",
    tags: ["Programs & Projects", "Community Engagement", "Operations", "Leadership"],
    bullets: [
      "Transformed a temporary carpark-based aid pilot into a university-scale food-relief program, growing weekly registrations from 400 to 8,000+ while preserving dignity and accessibility.",
      "Secured a dedicated commercial building and embedded the service into longer-term funding and operational frameworks while owning service design, communications, suppliers, intake logistics and crisis response for 12 months.",
      "Recruited and trained a rotating workforce of 400+ student and staff volunteers, creating the documentation, safety procedures and role clarity required for safe scale.",
      "Shared the operating model with universities nationally, contributing practical guidance to the broader tertiary student-support sector.",
    ],
  },
  {
    org: "University of Melbourne (GSA)",
    role: "Student Engagement Coordinator",
    period: "Apr 2021 – Aug 2021",
    location: "Melbourne, Australia",
    tags: ["Programs & Projects", "Community Engagement", "Digital Transformation"],
    bullets: [
      "Replaced paper-heavy club and program workflows with digital forms, databases and automated pipelines in Smartsheet and Eventbrite, reducing processing time by 20%.",
      "Managed the primary customer-service hub and complex escalations while coordinating Open Day, Orientation, Family Day, SWOTVAC and exam-support programs with student casual staff.",
    ],
  },
  {
    org: "Keypath Education",
    role: "Team Lead, Admissions",
    period: "Nov 2019 – Apr 2021",
    location: "Melbourne, Australia",
    tags: ["Leadership", "Operations"],
    bullets: [
      "Led high-volume recruitment pipelines using CRM dashboards and structured coaching; exceeded enrolment targets by up to 180%, achieved top results and supported new-starter onboarding.",
    ],
  },
  {
    org: "MiHaven Student Living",
    role: "Duty Manager",
    period: "Nov 2017 – Nov 2019",
    location: "Cairns, Australia",
    tags: ["Operations", "Community Engagement"],
    bullets: [
      "Directed after-hours operations for a residential student community, including safety, facilities escalation, compliance inspections, fire procedures and student wellbeing response.",
    ],
  },
  {
    org: "James Cook University",
    role: "Student Union President, Cairns Officer & Equity Officer",
    period: "Aug 2013 – Jun 2018",
    location: "Cairns, Australia",
    tags: ["Leadership", "Community Engagement", "Programs & Projects"],
    bullets: [
      "Represented student interests across institutional governance, clubs and societies, orientation, equity advocacy and frontline support, working directly with university executives and review panels.",
    ],
  },
];

export const CASE_STUDIES = [
  {
    id: "carpark-to-8000",
    title: "From a Carpark to 8,000+",
    org: "University of Melbourne",
    tags: ["Student Experience", "Systems", "Community Programs"],
    summary:
      "Scaling a university food-relief program from a folding table in a carpark to 8,000+ weekly student registrations — and designing the system so it kept running without a single indispensable person.",
    scale: "400 → 8,000+ weekly students · 400+ volunteers · 12 months solo coordination",
    status: "Featured",
  },
  {
    id: "orientation-scale",
    title: "University Orientation at Scale",
    org: "University of Melbourne Union",
    tags: ["Major Events", "University Operations"],
    summary:
      "5-day Orientation Week across 9 buildings, 2 theatres and an amphitheatre with 200+ vendor and club stalls.",
    scale: "145,000+ stakeholders · 200+ stalls · $1.2M+ sponsorship value",
    status: "Published",
  },
  {
    id: "calgary-pride",
    title: "Scaling Calgary Pride to 145,000+",
    org: "Calgary Pride",
    tags: ["Major Events", "Community Programs"],
    summary:
      "Directed a 24-event festival portfolio and city-centre parade reaching 145,000+ attendees at Prince's Island Park — coordinating 230+ volunteers, emergency services and municipal councils on a $1.75M festival environment.",
    scale: "145,000+ attendees · 230+ volunteers · $1.75M festival",
    status: "Published",
  },
  {
    id: "toronto-eventpro",
    title: "Rebuilding Ancillary Services at U of Toronto",
    org: "University of Toronto",
    tags: ["Systems", "University Operations", "Accessibility"],
    summary:
      "Configured Eventpro for institutional requirements, authored SOPs and video training manuals, and chaired cross-campus operations across IT, security, facilities, catering and the Dean of Students.",
    scale: "45+ casual staff · 24/7 on-call · Cross-campus governance",
    status: "Published",
  },
  {
    id: "monash-openday",
    title: "Monash Open Day — Five Portfolios, One System",
    org: "Monash University",
    tags: ["University Operations", "Student Experience"],
    summary:
      "End-to-end operational planning and execution of Monash Open Day across five high-impact portfolios — coordinating 300+ volunteers per semester and tracking engagement for priority cohorts.",
    scale: "5 portfolios · 300+ volunteers · UMAC-aligned",
    status: "Draft",
  },
  {
    id: "spreadsheet-to-systems",
    title: "From Spreadsheets to Systems",
    org: "University of Melbourne Union",
    tags: ["Systems", "Student Experience"],
    summary:
      "Modernised club registrations and student intake using Smartsheet and Eventbrite, reducing processing times by 20% and freeing frontline capacity for complex escalations.",
    scale: "20% faster processing · Documented workflows",
    status: "Published",
  },
];

export const SERVICES = [
  {
    id: "resilience-audit",
    n: "01",
    title: "Program Resilience Audit",
    lead:
      "A structured review of where a program relies too heavily on individuals, undocumented knowledge and informal processes.",
    deliverables: [
      "Stakeholder interviews",
      "Workflow & dependency mapping",
      "Documentation assessment",
      "Operational risk findings",
      "Resilience score",
      "30-day improvement plan",
    ],
    cta: "Request a Resilience Audit",
  },
  {
    id: "ai-readiness",
    n: "02",
    title: "Workflow & AI Readiness Review",
    lead:
      "An assessment of administrative workflows, accessibility, governance, manual effort and realistic opportunities for AI-supported improvement.",
    deliverables: [
      "Workflow mapping",
      "Administrative pain-point analysis",
      "AI opportunity assessment",
      "Accessibility & governance considerations",
      "Risk assessment",
      "Implementation roadmap",
    ],
    cta: "Discuss an AI Readiness Review",
  },
  {
    id: "event-os",
    n: "03",
    title: "Event Operating System Build",
    lead:
      "A complete operational framework for complex events, orientation programs, activations or recurring event portfolios.",
    deliverables: [
      "Master project plan & schedule",
      "Governance model & RACI",
      "Stakeholder & risk registers",
      "Event-day operating structure",
      "Communication & accessibility plans",
      "Templates and SOPs",
    ],
    cta: "Build my Event System",
  },
  {
    id: "student-programs",
    n: "04",
    title: "Program & Experience Design",
    lead:
      "Design or redesign of participant-facing programs — mentoring, orientation, inclusion, volunteering, food relief, peer support and community engagement — for universities, community organisations, festivals and complex operational teams.",
    deliverables: [
      "Program blueprint",
      "Service pathways",
      "Volunteer framework",
      "Accessibility-first design",
      "Measurement plan",
      "Handover documentation",
    ],
    cta: "Discuss a student program",
  },
  {
    id: "interim",
    n: "05",
    title: "Interim Program & Operations Leadership",
    lead:
      "Short-term senior support for organisations facing a major launch, leadership gap, complex project, rapid growth or operational instability.",
    deliverables: [
      "Situation assessment (week 1)",
      "Stabilisation plan",
      "Team & stakeholder alignment",
      "Delivery leadership",
      "Governance & reporting",
      "Handover to permanent lead",
    ],
    cta: "Discuss interim support",
  },
];

export const ENQUIRY_TYPES = [
  { value: "recruitment", label: "Recruitment opportunity" },
  { value: "consulting", label: "Consulting project" },
  { value: "ai_solutions", label: "JJ AI Solutions" },
  { value: "speaking", label: "Speaking or workshop" },
  { value: "partnership", label: "Partnership" },
  { value: "media", label: "Media" },
  { value: "general", label: "General enquiry" },
];

// Real event photography — used across Home ("In the field") and Case Studies pages.
// Every photo below is a UMSU event; captions have been verified with JJ.
export const EVENT_PHOTOS = [
  {
    id: "umsu-idahobit",
    src: "/assets/umsu-event-attendees.jpg",
    caption: "IDAHOBIT Day — UMSU",
    meta: "Inclusive events · Student experience",
    event: "IDAHOBIT Day celebration",
    year: "2022",
    location: "University of Melbourne · Parkville, VIC",
    slug: "orientation-scale",
  },
  {
    id: "umsu-babba",
    src: "/assets/umsu-event-stall.jpg",
    caption: "Bands & Brunch: BABBA — UMSU",
    meta: "ABBA tribute · Live music activation",
    event: "Bands & Brunch — BABBA (ABBA tribute)",
    year: "2022",
    location: "University of Melbourne · Parkville, VIC",
    slug: "orientation-scale",
  },
  {
    id: "umsu-eid",
    src: "/assets/umsu-event-activation.jpg",
    caption: "Eid Mubarak — UMSU",
    meta: "Cultural programming · Community-led",
    event: "Eid Mubarak celebration",
    year: "2022",
    location: "University of Melbourne · Parkville, VIC",
    slug: "orientation-scale",
  },
  {
    id: "umsu-african-drums",
    src: "/assets/umsu-event-crowd.jpg",
    caption: "Bands & Brunch: African drums — UMSU",
    meta: "Diversity celebration · Live music",
    event: "Bands & Brunch — African drums",
    year: "2022",
    location: "University of Melbourne · Parkville, VIC",
    slug: "orientation-scale",
  },
  {
    id: "umsu-start-of-uni",
    src: "/assets/umsu-event-team.jpg",
    caption: "Start of Uni Party — UMSU",
    meta: "Orientation Week · Campus life",
    event: "Start of Uni Party (Orientation Week)",
    year: "2022",
    location: "University of Melbourne · Parkville, VIC",
    slug: "orientation-scale",
  },
];

// slug → primary photo, for thumbnails on the Case Studies page.
// The dynamic OG endpoint at `${REACT_APP_BACKEND_URL}/api/og/case-studies/<slug>`
// renders a brand-consistent 1200×630 card, used as a safe placeholder for any
// case study that doesn't yet have a verified event photo.
const OG_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/og/case-studies`;

export const CASE_STUDY_IMAGE_BY_SLUG = {
  // Real UMSU orientation-era photo — the BABBA crowd fits an orientation-week story.
  "orientation-scale": "/assets/umsu-event-stall.jpg",
  // Real food-relief photo.
  "carpark-to-8000": "/assets/umsu-events-team.webp",
  // Placeholders until real photos are supplied — use branded OG card.
  "calgary-pride": `${OG_BASE}/calgary-pride`,
  "monash-openday": `${OG_BASE}/monash-openday`,
  // Real U of Toronto / Victoria University building (Emmanuel College).
  "toronto-eventpro": "https://customer-assets-4nw71qhi.emergentagent.net/job_96cae9c0-cbe5-49c5-ac2e-b283753668ea/artifacts/8vaibz4r_Vic%20uni%20Emmaniuel%20College%20building%20that%20I%20used%20to%20manage%20in%20one%20of%20my%20portfolios%2C%20whil%20business%20maanger.webp",
  "spreadsheet-to-systems": `${OG_BASE}/spreadsheet-to-systems`,
};

// Photos from the "From a Carpark to 8,000+" flagship food-relief story.
// Ordered to visually tell the transformation: team → work → institutional program.
export const FOOD_RELIEF_PHOTOS = [
  {
    id: "team",
    src: "/assets/umsu-events-team.webp",
    caption: "The team",
    meta: "Staff, volunteers & senior leaders",
  },
  {
    id: "operations",
    src: "/assets/umsu-orientation-festival.webp",
    caption: "The work",
    meta: "COVID-era operations · masked volunteer packs",
  },
  {
    id: "storefront",
    src: "/assets/umsu-orientation-week.webp",
    caption: "The system",
    meta: "Union Mart · a documented, dignified retail model",
  },
];

// Real peer testimonials from JJ's Keypath / UTS team.
// Attribution: first name + organisation (JJ's own preference).
// Curated for professional tone with two "humanity" quotes for balance.
export const TESTIMONIALS = [
  {
    id: "anna",
    quote:
      "You have been an amazing teacher, co-worker, leader. The moment I signed the contract with Keypath I had no idea HOW lucky I was by having you in my team.",
    name: "Anna",
    org: "Keypath / UTS",
    theme: "Leadership",
  },
  {
    id: "andres",
    quote:
      "Your kindness, team spirit and initiative are great assets. I am certain you will continue to excel in whatever role you undertake. Your work makes a huge difference to a lot of people.",
    name: "Andres",
    org: "Keypath / UTS",
    theme: "Impact",
  },
  {
    id: "tran",
    quote:
      "From the very beginning you've been the most collaborative and enthusiastic team member. You're a wonderful person set to achieve many great things.",
    name: "Tran",
    org: "Keypath / UTS",
    theme: "Collaboration",
  },
  {
    id: "joey",
    quote:
      "You've been a great support and UTS certainly would not be where it is today without your contribution.",
    name: "Joey",
    org: "Keypath / UTS",
    theme: "Contribution",
  },
  {
    id: "antonia",
    quote:
      "You can tell that you are really passionate about what you do. Your energy and laughter is infectious!",
    name: "Antonia",
    org: "Keypath / UTS",
    theme: "Energy",
  },
  {
    id: "val",
    quote:
      "You are such an amazing colleague and a wonderful friend. You give everything your all and I just know that you will be successful.",
    name: "Val",
    org: "Keypath / UTS",
    theme: "Commitment",
  },
  {
    id: "ravi",
    quote:
      "It was great working and learning with you. Thank you for always being there for the team.",
    name: "Ravi",
    org: "Keypath / UTS",
    theme: "Team",
  },
  {
    id: "samantha",
    quote:
      "How warm you were to me when I started, offering help as though you could read my mind — a pretty clear testament to your kindness.",
    name: "Samantha",
    org: "Keypath / UTS",
    theme: "Warmth",
  },
  {
    id: "laure",
    quote:
      "I feel so lucky to have met you and worked with you. I am grateful to be able to call you not only my workmate but also my friend.",
    name: "Laure",
    org: "Keypath / UTS",
    theme: "Human",
  },
  {
    id: "umsu-structure",
    quote:
      "You brought structure to the program and really improved the experience for us and the students. I appreciate how approachable you are. You genuinely care for the students — you have advocated for the extension of the program many, many times.",
    name: "UMSU Food Relief team member",
    org: "University of Melbourne",
    theme: "Systems",
  },
  {
    id: "umsu-without-you",
    quote:
      "We're so glad you're back. Truly appreciate all the hard work you do — especially now that we've experienced two weeks without you. Not sure where the team would be without you.",
    name: "UMSU Food Relief team member",
    org: "University of Melbourne",
    theme: "Continuity",
  },
  {
    id: "umsu-leadership",
    quote:
      "I really appreciate how responsive you are to all the feedback which makes people feel heard. A great leadership for such a young age.",
    name: "UMSU Food Relief team member",
    org: "University of Melbourne",
    theme: "Leadership",
  },
  {
    id: "umsu-dedication",
    quote:
      "I admire your passion and resilience in making students' lives easier at uni. We are so grateful for your dedication, commitment, and the ability to pause and smile and be kind to us.",
    name: "UMSU Food Relief team member",
    org: "University of Melbourne",
    theme: "Impact",
  },
  // ---- About page approved featured + carousel (JJ-approved verbatim) ----
  {
    id: "shawn-kazubowski-houston",
    quote:
      "JJ is an absolute pleasure to work with. He is a consummate professional and fantastic colleague.",
    name: "Shawn Kazubowski-Houston",
    title: "Assistant Registrar & Placement Support Officer",
    org: "Emmanuel College of Victoria University · University of Toronto",
    theme: "Systems & Transformation",
    source: "LinkedIn · 29 Aug 2025",
    relationship: "Recommended JJ's leadership of the EventPro implementation across the Victoria University portfolio.",
  },
  {
    id: "barbra-yan",
    quote:
      "From the moment I joined UMSU, JJ's welcoming personality put me at ease and made me feel a sense of belonging in the team.",
    name: "Barbra Yan",
    title: "Former UMSU colleague",
    org: "University of Melbourne",
    theme: "People Leadership & Inclusion",
    source: "LinkedIn · 6 Apr 2024",
    fullQuote:
      "JJ is an outstanding manager at UMSU. His warm-hearted and approachable character has always made him a pleasure to work with. From the moment I joined UMSU, JJ's welcoming personality put me at ease and made me feel a sense of belonging in the team. He not only takes care of all team members at all times but also consistently generates fantastic ideas for events. As an international student, I'm so grateful for JJ's understanding and support. His genuine care and consideration make me feel valued and supported. With his exceptional skills and dedication, I have no doubt that JJ will continue to thrive in his future careers. I give my best wishes to him and am grateful for the opportunity to work with him at UMSU.",
  },
  {
    id: "bavatharani-maheswaran",
    quote:
      "JJ possesses a unique blend of strategic vision, meticulous attention to detail, and a genuine passion for creating memorable experiences.",
    name: "Bavatharani Maheswaran",
    title: "Former events team member",
    org: "University of Melbourne (UMSU)",
    theme: "Operations & Event Leadership",
    source: "LinkedIn · 12 Apr 2024",
    fullQuote:
      "I have had the privilege of working under JJ as the manager of our events team, and I can attest to his exceptional leadership skills. JJ brings a wealth of experience and dedication to every project, ensuring that our team consistently delivers high-quality events. JJ possesses a unique blend of strategic vision, meticulous attention to detail, and a genuine passion for creating memorable experiences. Beyond his professional expertise, JJ is also a genuine mentor and advocate for his team members. His approachable demeanor and willingness to listen makes him not just a manager, but a trusted advisor and confidant. It's an honor to work alongside JJ, and I have no doubt that his leadership will continue to inspire and drive success wherever he goes.",
  },
  {
    id: "victoria-mercy-kataike",
    quote:
      "I was well trained for my duties as a Food relief casual staff, a position I still hold and thrive in today thanks to his exceptional leadership.",
    name: "Victoria Mercy Kataike, PhD",
    title: "Former Food Relief program team member",
    org: "University of Melbourne",
    theme: "Community Impact & Staff Development",
    source: "LinkedIn · 4 Apr 2024",
    fullQuote:
      "JesseJames is an experienced, understanding and passionate manager. I started working at the Food relief program when I was barely a year into my studies at the University of Melbourne. Being an international student, he made me feel welcome, equipped for the job, and introduced me to a whole new community of students like myself, something I would achieve on my own. I was well trained for my duties as a Food relief casual staff, a position I still hold and thrive in today thanks to his exceptional leadership. His desire and persistence for this program to continue past the COVID - 19 pandemic has seen it become a fully recognised program at the University of Melbourne.",
  },
  // ---- LinkedIn endorsements from managers, directors and senior peers ----
  {
    id: "matt-posetti",
    quote:
      "Over the journey you have remained an energetic and proactive team member, supporting events and services of real and substantial value. From orientation activities, through to cultural festivals, our critically important food security programs, through to a range of health and well-being initiatives — you have proved tireless, passionate, and a committed student experience advocate.",
    name: "Matt Posetti",
    title: "Deputy Director Student Engagement",
    org: "University of Melbourne",
    theme: "Leadership",
    source: "LinkedIn",
  },
  {
    id: "reshmee-gunesh",
    quote:
      "This isn't sad news at all. Unimelb gets to keep you, see you grow and work in an area you're clearly passionate about — and we get to work together to create the best experiences for student success.",
    name: "Reshmee Gunesh",
    title: "Associate Director",
    org: "University of Melbourne",
    theme: "Strategy",
    source: "LinkedIn",
  },
  {
    id: "caty-li",
    quote:
      "I'm so lucky to have you as a colleague. The role you've played in helping me adjust to my role is huge. I'm grateful for all the help and support you've provided me. You will be dearly missed.",
    name: "Caty (Wenfei) Li",
    title: "Senior Advisor, Students@Work · LinkedIn Top Voice",
    org: "University of Melbourne",
    theme: "Mentorship",
    source: "LinkedIn",
  },
  {
    id: "aviya-b",
    quote:
      "I'm looking forward to another year of working with you on some unforgettable events.",
    name: "Aviya B.",
    title: "Stakeholder Engagement · Major Events",
    org: "University of Melbourne",
    theme: "Events",
    source: "LinkedIn",
  },
  {
    id: "christine-kan",
    quote: "A great role for an event champion like you.",
    name: "Christine Kan",
    title: "Program & Event Management · Higher Education & the Arts",
    org: "Sector peer",
    theme: "Sector",
    source: "LinkedIn",
  },
  {
    id: "ivana-e",
    quote: "You're going to be a great asset to the team.",
    name: "Ivana Eleftheriadis",
    title: "Owner, Melon Marketing",
    org: "External",
    theme: "External",
    source: "LinkedIn",
  },
  {
    id: "angeline-chan",
    quote: "Can't wait to see what you do in the space. Well deserved.",
    name: "Angeline Chan",
    title: "Communications Specialist",
    org: "University of Melbourne",
    theme: "Peer",
    source: "LinkedIn",
  },
  {
    id: "andrew-king",
    quote:
      "You're going to be a hard one to replace. Thank you for your expert approach to everything you do — your collaboration with all areas of UOM has not gone unnoticed. The University, staff and students are in a much better place with the work you have done.",
    name: "Andrew King",
    title: "Physical Security Manager",
    org: "University of Melbourne",
    theme: "Operations",
    source: "LinkedIn",
  },
  {
    id: "catherine-mann",
    quote:
      "You bring joyfulness and organisational brilliance to everything you do. Seeing the results of your passion for working with students has been a highlight of walking across the campus and seeing UMSU everywhere.",
    name: "Catherine Mann",
    title: "Acting Head, Wilam Hall",
    org: "University of Melbourne",
    theme: "Leadership",
    source: "LinkedIn",
  },
  {
    id: "emily-re",
    quote:
      "I really appreciated your positive and open contributions to the self-awareness sessions with the TREV foundation. Remember your strengths in emotional intelligence and keep up your fabulous energy around justice, inclusion, empathy and compassion.",
    name: "Emily Re",
    title: "Executive Coach — NFP leaders & social enterprises",
    org: "External",
    theme: "Emotional intelligence",
    source: "LinkedIn",
  },
  {
    id: "gsa-org",
    quote:
      "You've done some amazing work and will be missed by many here at the University. Sounds like you have some exciting things coming up.",
    name: "Graduate Student Association",
    title: "University of Melbourne · Official",
    org: "University of Melbourne",
    theme: "Institutional",
    source: "LinkedIn",
  },
  {
    id: "ellie-macaulay",
    quote:
      "It has been such fun collaborating with you and picking your brain about all things events. You'll be an asset wherever you go.",
    name: "Ellie Macaulay",
    title: "Event Coordinator",
    org: "Melbourne University Sport",
    theme: "Events",
    source: "LinkedIn",
  },
];


// Recruiter page — role categories from the problem statement + résumé pathways
// mapped to the actual CV files uploaded by JJ.
export const TARGET_ROLES = [
  "Senior Program Manager",
  "Senior Project Manager",
  "Student Experience Leadership",
  "University Operations",
  "Events & Engagement Leadership",
  "Accessibility & Inclusion Programs",
  "Business Manager",
  "Divisional Manager",
  "Community & Not-for-Profit Leadership",
  "Operational Transformation",
];

export const RESUME_PATHWAYS = []; // Retired — Batch 1 §12 consolidated to a single canonical Executive CV. See <CVBlock />.


// Third-party awards & commendations with certificate imagery.
export const AWARDS = [
  {
    id: "vol-vic-2021",
    title: "Finalist — Inclusive Volunteering Award",
    org: "Volunteering Victoria · 2021 Volunteering Awards",
    year: "2021",
    context: "Recognised at the state level for community-support work during COVID-19.",
    image: "/assets/uoft-victoria-volunteer.jpg",
    tier: "State-level recognition",
  },
  {
    id: "keypath-sp1-2021",
    title: "Highest Performer — UTS",
    org: "Keypath Education",
    year: "SP1 2021",
    context: "178% to target — highest performer across the UTS portfolio.",
    image: "/assets/keypath-results-4.jpg",
    tier: "Commendation",
    metric: "178%",
  },
  {
    id: "keypath-tp3-2020",
    title: "Highest Enrolments — UTS",
    org: "Keypath Education",
    year: "TP3 2020",
    context: "180% to target — highest enrolments across the UTS portfolio.",
    image: "/assets/keypath-results-1.jpg",
    tier: "Commendation",
    metric: "180%",
  },
  {
    id: "keypath-sp5-2020",
    title: "Highest Enrolments — UTS",
    org: "Keypath Education",
    year: "SP5 2020",
    context: "121% to target — highest enrolments across the UTS portfolio.",
    image: "/assets/keypath-results-2.jpg",
    tier: "Commendation",
    metric: "121%",
  },
  {
    id: "keypath-sp6-2020",
    title: "Above Target — UTS",
    org: "Keypath Education",
    year: "SP6 2020",
    context: "105% to target across the UTS portfolio.",
    image: "/assets/keypath-results-3.jpg",
    tier: "Commendation",
    metric: "105%",
  },
];


// Canada chapter — lived, on-the-ground work across Toronto & Calgary.
// Photos: Australian High Commissioner reception (Toronto), U of T portfolio team,
// Toronto Black Film Festival, colleague portraits and Victoria College event.
export const CANADA_CHAPTER = {
  eyebrow: "Canada chapter",
  headline: "Toronto, Calgary,",
  headlineAccent: "and the rooms in between.",
  intro:
    "Nine months across two Canadian cities — a manager role at Calgary Pride ($1.75M festival, 145K attendees), an ancillary services portfolio at the University of Toronto (94 FTE, $2.8M operating budget), and the professional and diplomatic circles that came with them. AODA-aware, trauma-informed, mental-health-first-aid certified in Canada.",
  eligibility: [
    { label: "Rights", value: "Australian citizen — unrestricted Australian work rights" },
    { label: "International", value: "Subject to destination; employer sponsorship may be required" },
    { label: "Mobility", value: "Open to relocation · Toronto / Ontario / Alberta preferred" },
    { label: "Framework knowledge", value: "AODA · Trauma-informed practice · MHFA (Canada)" },
    { label: "Cities lived in", value: "Toronto · Calgary" },
  ],
  photos: [
    {
      src: "/assets/jcu-canada-representation-invite.jpg",
      alt: "Invitation from the Australian High Commissioner to Canada — Australian university alumni reception, Toronto",
      caption: "Australian High Commissioner networking reception · Toronto · Sep 2025",
      wide: true,
    },
    {
      src: "/assets/jjs-community-support-2.jpg",
      alt: "JJ Argent with the Australian High Commissioner to Canada at the Victoria College reception, University of Toronto",
      caption: "With the Australian High Commissioner · Victoria College reception · U of T",
    },
    {
      src: "/assets/jjs-community-support-1.jpg",
      alt: "JJ Argent representing James Cook University at the Australian alumni reception in Toronto",
      caption: "Representing James Cook University · Australian alumni reception · Toronto",
    },
    {
      src: "/assets/uoft-victoria-team-christmas.jpg",
      alt: "Victoria College (University of Toronto) team Christmas photo",
      caption: "Victoria College team · U of T · Toronto",
    },
    {
      src: "/assets/uoft-toronto-black-film-festival.jpg",
      alt: "Toronto Black Film Festival — event we delivered with staff member Princess",
      caption: "Toronto Black Film Festival · Delivered with the Victoria College team",
    },
    {
      src: "/assets/uoft-eventpro-training.jpg",
      alt: "Eventpro training with the implementation officer — Victoria College, U of T",
      caption: "Eventpro training · Victoria College · U of T",
    },
    {
      src: "/assets/uoft-victoria-summer-residence-social.jpg",
      alt: "Victoria College summer residence staff social outing — Toronto",
      caption: "Summer residence staff social · Victoria College · Toronto",
    },
    {
      src: "https://customer-assets-4nw71qhi.emergentagent.net/job_96cae9c0-cbe5-49c5-ac2e-b283753668ea/artifacts/yngy8mf0_Vic%20uni%20dining%20hall%20building%20that%20I%20used%20to%20manage%20in%20one%20of%20my%20portfolios%2C%20whil%20business%20maanger.jpg",
      alt: "Victoria College dining hall — one of the 17 buildings in JJ's portfolio",
      caption: "Dining hall · Victoria College · U of T",
    },
    {
      src: "https://customer-assets-4nw71qhi.emergentagent.net/job_96cae9c0-cbe5-49c5-ac2e-b283753668ea/artifacts/123o5tnk_Vic%20uni%20Isabell%20Bader%20theatre%20building%20that%20I%20used%20to%20manage%20in%20one%20of%20my%20portfolios%2C%20whil%20business%20maanger.jpg",
      alt: "Isabel Bader Theatre — Victoria College, University of Toronto",
      caption: "Isabel Bader Theatre · Victoria College · U of T",
    },
    {
      src: "https://customer-assets-4nw71qhi.emergentagent.net/job_96cae9c0-cbe5-49c5-ac2e-b283753668ea/artifacts/cicywm7s_Vic%20uni%20Burwash%20Residence%20building%20that%20I%20used%20to%20manage%20in%20one%20of%20my%20portfolios%2C%20whil%20business%20maanger.jpg",
      alt: "Burwash Residence — Victoria College, University of Toronto",
      caption: "Burwash Residence · Victoria College · U of T",
    },
  ],
};


// Certifications — surfaced on About page (also referenced by Canada CV).
export const CERTIFICATIONS = [
  {
    id: "mhfa-canada",
    title: "Mental Health First Aid",
    org: "Mental Health Commission of Canada",
    year: "2025",
    region: "Canada",
  },
  {
    id: "trauma-informed-alberta",
    title: "Trauma-Informed Care Certificate",
    org: "Recovery Alberta",
    year: "2024",
    region: "Canada",
  },
  {
    id: "first-aid-cpr",
    title: "First Aid & CPR",
    org: "Shirt Courses Australia",
    year: "2024",
    region: "Australia",
  },
  {
    id: "tesol",
    title: "TESOL Certification",
    org: "International Open Academy",
    year: "2020",
    region: "International",
  },
];




// Speaking page — three featured talks.
// Formats: K = Keynote · P = Panel · W = Workshop · I = Internal leadership session
export const SPEAKING_TOPICS = [
  {
    id: "built-to-run",
    n: "01",
    title: "Built to Run Without Me",
    body: "The signature talk. Why the best measure of a leader is not how essential they are — but how quickly the room stops needing them. Practical patterns for designing indispensable programs, not indispensable people.",
    audience: "Executive teams · Boards · University leadership · Not-for-profit CEOs",
    formats: ["Keynote", "Internal leadership session"],
    signature: true,
  },
  {
    id: "carpark-to-8000",
    n: "02",
    title: "From a Carpark to 8,000+",
    body: "The full story of scaling a university food-relief program from a folding table to 8,000+ weekly students — and the systems that let it keep running when I couldn't.",
    audience: "University staff · Student experience teams · Community organisations",
    formats: ["Keynote", "Panel"],
  },
  {
    id: "practical-ai",
    n: "03",
    title: "Practical AI for University Operations",
    body: "Where AI actually helps university operations, where it doesn't, and how to pilot without breaking governance, accessibility or trust.",
    audience: "Higher-ed operations · CIO teams · Academic governance",
    formats: ["Keynote", "Workshop", "Internal leadership session"],
  },
];
